/**
 * StudentFlagsEditor — modal for adding/removing flags on a student.
 *
 * Shows a checkbox list of all active flag types.
 * Pre-checks those already assigned to the student.
 * On save: writes the full set of selected flag IDs to the student document.
 */

import { useState, useEffect } from 'react';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../../firebase.js';
import { flagTypesService } from '../../../services/flagTypesService.js';

const StudentFlagsEditor = ({ student, schoolId, onClose }) => {
  const [flagTypes, setFlagTypes] = useState([]);
  const [selected, setSelected] = useState(new Set(student.flags || []));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    flagTypesService.getAll(schoolId).then(types => {
      if (!cancelled) {
        setFlagTypes(types);
        setLoading(false);
      }
    });
    return () => { cancelled = true; };
  }, [schoolId]);

  const toggle = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const studentRef = doc(db, 'schools', schoolId, 'students', student.id);
      await updateDoc(studentRef, { flags: Array.from(selected) });
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <dialog open className="modal modal-open" aria-modal="true">
      <div className="modal-box">
        <h3 className="font-bold text-lg mb-4">Edit Student Flags</h3>

        {loading ? (
          <p className="text-base-content/60">Loading flags…</p>
        ) : flagTypes.length === 0 ? (
          <p className="text-base-content/60 italic">No flag types defined for this school.</p>
        ) : (
          <div className="space-y-2">
            {flagTypes.map(ft => (
              <label key={ft.id} className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  className="checkbox checkbox-sm"
                  checked={selected.has(ft.id)}
                  onChange={() => toggle(ft.id)}
                />
                <span>{ft.name}</span>
              </label>
            ))}
          </div>
        )}

        <div className="modal-action">
          <button
            type="button"
            className="btn btn-ghost"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleSave}
            disabled={saving || loading}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </dialog>
  );
};

export default StudentFlagsEditor;
